"use client";

import { useState, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Stack,
  Fade,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Collapse,
} from "@mui/material";
import {
  ViewAgenda,
  Delete,
  Download,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  Image as ImageIcon,
} from "@mui/icons-material";

type ImageFormat = "PNG" | "JPEG" | "WebP" | "SVG";

const BARCODE_FORMATS: { value: string; label: string; hint?: string }[] = [
  { value: "CODE128", label: "CODE128 (auto)", hint: "Any text, auto A/B/C" },
  { value: "CODE128A", label: "CODE128 A", hint: "Uppercase, control chars" },
  { value: "CODE128B", label: "CODE128 B", hint: "Any ASCII" },
  { value: "CODE128C", label: "CODE128 C", hint: "Pairs of digits" },
  { value: "CODE39", label: "CODE39", hint: "A–Z, 0–9, -.$/+% space" },
  { value: "CODE93", label: "CODE93", hint: "Similar to CODE39" },
  { value: "EAN13", label: "EAN-13", hint: "12 or 13 digits" },
  { value: "EAN8", label: "EAN-8", hint: "7 or 8 digits" },
  { value: "EAN5", label: "EAN-5", hint: "5 digits (supplemental)" },
  { value: "EAN2", label: "EAN-2", hint: "2 digits (supplemental)" },
  { value: "UPC", label: "UPC-A", hint: "11 or 12 digits" },
  { value: "UPCE", label: "UPC-E", hint: "6–12 digits" },
  { value: "ITF", label: "ITF", hint: "Even number of digits" },
  { value: "ITF14", label: "ITF-14", hint: "13 or 14 digits" },
  { value: "MSI", label: "MSI", hint: "Numeric" },
  { value: "MSI10", label: "MSI-10", hint: "Numeric" },
  { value: "MSI11", label: "MSI-11", hint: "Numeric" },
  { value: "MSI1010", label: "MSI-1010", hint: "Numeric" },
  { value: "MSI1110", label: "MSI-1110", hint: "Numeric" },
  { value: "pharmacode", label: "Pharmacode", hint: "1–6 digits" },
  { value: "codabar", label: "Codabar", hint: "0–9, A–D, -$:/.+" },
];

function svgToDataUrl(svg: SVGElement): string {
  const s = new XMLSerializer().serializeToString(svg);
  return "data:image/svg+xml," + encodeURIComponent(s);
}

function svgToCanvasDataUrl(
  svg: SVGElement,
  mime: "image/png" | "image/jpeg" | "image/webp",
  quality?: number,
  scale: number = 2
): Promise<string> {
  return new Promise((resolve, reject) => {
    const dataUrl = svgToDataUrl(svg);
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth * scale;
      const h = img.naturalHeight * scale;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas 2d not available"));
        return;
      }
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      const out = canvas.toDataURL(mime, quality);
      resolve(out);
    };
    img.onerror = () => reject(new Error("Failed to load SVG as image"));
    img.src = dataUrl;
  });
}

export default function TextToBarcodeConverter() {
  const svgRef = useRef<SVGSVGElement>(null);
  const [input, setInput] = useState("");
  const [hasBarcode, setHasBarcode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [format, setFormat] = useState("CODE128");
  const [width, setWidth] = useState(2);
  const [height, setHeight] = useState(100);
  const [displayValue, setDisplayValue] = useState(true);
  const [lineColor, setLineColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#FFFFFF");
  const [fontSize, setFontSize] = useState(20);
  const [textPosition, setTextPosition] = useState<"top" | "bottom">("bottom");
  const [margin, setMargin] = useState(10);
  const [downloadFormat, setDownloadFormat] = useState<ImageFormat>("PNG");
  const [quality, setQuality] = useState(0.92);
  const [exportScale, setExportScale] = useState(2);

  const currentHint = BARCODE_FORMATS.find((f) => f.value === format)?.hint;

  const generate = useCallback(async () => {
    setError(null);
    const text = input.trim();
    if (!text) {
      setError("Please enter text to encode.");
      return;
    }
    if (!svgRef.current) return;
    try {
      const JsBarcode = (await import("jsbarcode")).default;
      JsBarcode(svgRef.current, text, {
        format,
        width,
        height,
        displayValue,
        lineColor,
        background: bgColor,
        fontSize,
        textPosition,
        margin,
      });
      setHasBarcode(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Invalid input for this barcode format."
      );
      setHasBarcode(false);
    }
  }, [
    input,
    format,
    width,
    height,
    displayValue,
    lineColor,
    bgColor,
    fontSize,
    textPosition,
    margin,
  ]);

  const handleDownload = useCallback(async () => {
    if (!svgRef.current || !hasBarcode) return;
    const ext = downloadFormat.toLowerCase();
    const mime =
      downloadFormat === "PNG"
        ? "image/png"
        : downloadFormat === "JPEG"
          ? "image/jpeg"
          : "image/webp";

    if (downloadFormat === "SVG") {
      const s = new XMLSerializer().serializeToString(svgRef.current);
      const blob = new Blob([s], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `barcode.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }
    const dataUrl = await svgToCanvasDataUrl(
      svgRef.current,
      mime as "image/png" | "image/jpeg" | "image/webp",
      downloadFormat === "JPEG" || downloadFormat === "WebP"
        ? quality
        : undefined,
      exportScale
    );
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `barcode.${ext}`;
    a.click();
  }, [hasBarcode, downloadFormat, quality, exportScale]);

  const handleCopy = useCallback(async () => {
    if (!svgRef.current || !hasBarcode) return;
    try {
      if (downloadFormat === "SVG") {
        const s = new XMLSerializer().serializeToString(svgRef.current);
        await navigator.clipboard.writeText(s);
      } else {
        const mime =
          downloadFormat === "PNG"
            ? "image/png"
            : downloadFormat === "JPEG"
              ? "image/jpeg"
              : "image/webp";
        const dataUrl = await svgToCanvasDataUrl(
          svgRef.current,
          mime,
          downloadFormat === "JPEG" || downloadFormat === "WebP"
            ? quality
            : undefined,
          exportScale
        );
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        await navigator.clipboard.write([
          new ClipboardItem({ [blob.type]: blob }),
        ]);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Failed to copy to clipboard.");
    }
  }, [hasBarcode, downloadFormat, quality, exportScale]);

  const handleClear = () => {
    setInput("");
    setHasBarcode(false);
    setError(null);
    setCopied(false);
    if (svgRef.current) {
      svgRef.current.innerHTML = "";
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Text to encode
        </Typography>
        <TextField
          multiline
          rows={3}
          fullWidth
          placeholder="Enter text or numbers (e.g. 1234567890128 for EAN-13, or any text for CODE128)..."
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
            if (hasBarcode) setHasBarcode(false);
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontFamily: "monospace",
              fontSize: "0.9rem",
            },
          }}
        />

        {/* Main options */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Barcode format</InputLabel>
              <Select
                value={format}
                label="Barcode format"
                onChange={(e) => {
                  setFormat(e.target.value);
                  if (hasBarcode) setHasBarcode(false);
                }}
              >
                {BARCODE_FORMATS.map((f) => (
                  <MenuItem key={f.value} value={f.value}>
                    {f.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {currentHint && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                {currentHint}
              </Typography>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="body2" gutterBottom>
              Bar width: {width}
            </Typography>
            <Slider
              value={width}
              onChange={(_, v) => {
                setWidth(v as number);
                if (hasBarcode) setHasBarcode(false);
              }}
              min={1}
              max={4}
              step={0.5}
              valueLabelDisplay="auto"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="body2" gutterBottom>
              Height: {height} px
            </Typography>
            <Slider
              value={height}
              onChange={(_, v) => {
                setHeight(v as number);
                if (hasBarcode) setHasBarcode(false);
              }}
              min={50}
              max={200}
              step={10}
              valueLabelDisplay="auto"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Download as</InputLabel>
              <Select
                value={downloadFormat}
                label="Download as"
                onChange={(e) =>
                  setDownloadFormat(e.target.value as ImageFormat)
                }
              >
                <MenuItem value="PNG">PNG</MenuItem>
                <MenuItem value="JPEG">JPEG</MenuItem>
                <MenuItem value="WebP">WebP</MenuItem>
                <MenuItem value="SVG">SVG</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={displayValue}
                  onChange={(e) => {
                    setDisplayValue(e.target.checked);
                    if (hasBarcode) setHasBarcode(false);
                  }}
                />
              }
              label="Show value below barcode"
            />
          </Grid>
        </Grid>

        {/* Quality for JPEG/WebP */}
        {(downloadFormat === "JPEG" || downloadFormat === "WebP") && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" gutterBottom>
              Quality: {Math.round(quality * 100)}%
            </Typography>
            <Slider
              value={quality}
              onChange={(_, v) => setQuality(v as number)}
              min={0.1}
              max={1}
              step={0.05}
              valueLabelDisplay="auto"
              size="small"
              sx={{ maxWidth: 280 }}
            />
          </Box>
        )}

        {/* Advanced */}
        <Button
          size="small"
          startIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setShowAdvanced(!showAdvanced)}
          sx={{ mt: 2 }}
        >
          {showAdvanced ? "Hide" : "Show"} advanced options
        </Button>
        <Collapse in={showAdvanced}>
          <Grid container spacing={3} sx={{ mt: 1, mb: 1 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2">Line color</Typography>
                <input
                  type="color"
                  value={lineColor}
                  onChange={(e) => {
                    setLineColor(e.target.value);
                    if (hasBarcode) setHasBarcode(false);
                  }}
                  style={{
                    width: 40,
                    height: 32,
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                />
                <TextField
                  size="small"
                  value={lineColor}
                  onChange={(e) => {
                    setLineColor(e.target.value);
                    if (hasBarcode) setHasBarcode(false);
                  }}
                  sx={{ width: 100 }}
                  inputProps={{ maxLength: 7 }}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2">Background</Typography>
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => {
                    setBgColor(e.target.value);
                    if (hasBarcode) setHasBarcode(false);
                  }}
                  style={{
                    width: 40,
                    height: 32,
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                />
                <TextField
                  size="small"
                  value={bgColor}
                  onChange={(e) => {
                    setBgColor(e.target.value);
                    if (hasBarcode) setHasBarcode(false);
                  }}
                  sx={{ width: 100 }}
                  inputProps={{ maxLength: 7 }}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Text position</InputLabel>
                <Select
                  value={textPosition}
                  label="Text position"
                  onChange={(e) => {
                    setTextPosition(e.target.value as "top" | "bottom");
                    if (hasBarcode) setHasBarcode(false);
                  }}
                >
                  <MenuItem value="bottom">Below</MenuItem>
                  <MenuItem value="top">Above</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="body2" gutterBottom>
                Font size: {fontSize}
              </Typography>
              <Slider
                value={fontSize}
                onChange={(_, v) => {
                  setFontSize(v as number);
                  if (hasBarcode) setHasBarcode(false);
                }}
                min={10}
                max={40}
                step={2}
                valueLabelDisplay="auto"
                size="small"
                sx={{ maxWidth: 280 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="body2" gutterBottom>
                Margin: {margin}
              </Typography>
              <Slider
                value={margin}
                onChange={(_, v) => {
                  setMargin(v as number);
                  if (hasBarcode) setHasBarcode(false);
                }}
                min={0}
                max={20}
                step={1}
                valueLabelDisplay="auto"
                size="small"
                sx={{ maxWidth: 280 }}
              />
            </Grid>
            {downloadFormat !== "SVG" && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="body2" gutterBottom>
                  Export scale: {exportScale}× (higher = sharper)
                </Typography>
                <Slider
                  value={exportScale}
                  onChange={(_, v) => setExportScale(v as number)}
                  min={1}
                  max={4}
                  step={1}
                  valueLabelDisplay="auto"
                  size="small"
                  sx={{ maxWidth: 280 }}
                />
              </Grid>
            )}
          </Grid>
        </Collapse>

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 3 }}
          flexWrap="wrap"
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<ViewAgenda sx={{ color: "#ffffff" }} />}
            onClick={generate}
            disabled={!input.trim()}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: "1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            Generate Barcode
          </Button>
          <Button
            variant="outlined"
            startIcon={<Delete />}
            onClick={handleClear}
          >
            Clear
          </Button>
        </Stack>

        <Fade in={hasBarcode}>
          <Box sx={{ mt: 4, display: hasBarcode ? "block" : "none" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                mb: 2,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Barcode
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  size="small"
                  startIcon={<Download />}
                  onClick={handleDownload}
                  variant="outlined"
                >
                  Download {downloadFormat}
                </Button>
                <Button
                  size="small"
                  startIcon={copied ? <CheckCircle /> : <ImageIcon />}
                  onClick={handleCopy}
                  variant="outlined"
                  color={copied ? "success" : "primary"}
                >
                  {copied
                    ? "Copied!"
                    : downloadFormat === "SVG"
                      ? "Copy SVG"
                      : "Copy image"}
                </Button>
              </Stack>
            </Box>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: bgColor,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 140,
                overflow: "auto",
              }}
            >
              <svg
                ref={svgRef}
                style={{ maxWidth: "100%", height: "auto", display: "block" }}
              />
            </Paper>
          </Box>
        </Fade>
      </Paper>
    </Box>
  );
}
